import {
  type Attendee,
  type CalendarConnection,
  type CalendarError,
  CalendarErrorType,
  type CalendarEvent,
  type CalendarProvider,
  type RecurrencePattern,
} from '../types/calendar';
import { calendarErrorHandler } from './calendarErrorHandler';

interface GoogleCalendarEvent {
  id?: string;
  summary?: string;
  description?: string;
  location?: string;
  htmlLink?: string;
  hangoutLink?: string;
  start?: { dateTime?: string; date?: string };
  end?: { dateTime?: string; date?: string };
  attendees?: GoogleAttendee[];
  recurrence?: string[];
  conferenceData?: Record<string, unknown>;
}

interface GoogleAttendee {
  email?: string;
  displayName?: string;
  responseStatus?: string;
}

export interface GoogleCalendarConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  scopes: string[];
}

export interface GoogleTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
}

export interface GoogleCalendarAuthResult {
  success: boolean;
  connection?: CalendarConnection;
  error?: CalendarError;
}

export class GoogleCalendarService {
  private static readonly GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
  private static readonly GOOGLE_TOKEN_URL = 'https://oauth2.googleapis.com/token';
  private static readonly GOOGLE_CALENDAR_API_URL = 'https://www.googleapis.com/calendar/v3';

  private config: GoogleCalendarConfig;

  constructor(config: GoogleCalendarConfig) {
    this.config = {
      ...config,
      scopes: config.scopes || [
        'https://www.googleapis.com/auth/calendar.readonly',
        'https://www.googleapis.com/auth/calendar.events',
      ],
    };
  }

  /**
   * Generate Google OAuth URL with calendar scopes for user authorization
   * Requirement 5.1: Initiate Google calendar OAuth flow
   */
  public initiateAuth(state?: string): string {
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      response_type: 'code',
      scope: this.config.scopes.join(' '),
      access_type: 'offline',
      prompt: 'consent',
      ...(state && { state }),
    });

    return `${GoogleCalendarService.GOOGLE_AUTH_URL}?${params.toString()}`;
  }

  /**
   * Exchange authorization code for access and refresh tokens
   * Requirement 5.2: Handle OAuth callback and token exchange
   */
  public async handleCallback(
    authorizationCode: string,
    userId: string,
  ): Promise<GoogleCalendarAuthResult> {
    try {
      return await calendarErrorHandler
        .handleConnectionError(
          'google',
          async () => {
            const tokenResponse = await this.exchangeCodeForTokens(authorizationCode);

            if (!(tokenResponse.success && tokenResponse.tokens)) {
              throw calendarErrorHandler.createError(
                CalendarErrorType.AUTH_FAILED,
                'Failed to exchange authorization code for tokens',
                true,
                'google',
              );
            }

            const connection = await this.createConnection(userId, tokenResponse.tokens);

            // Store connection securely
            await this.storeConnection(connection);

            return connection;
          },
          userId,
        )
        .then((connection) => ({
          success: true,
          connection,
        }));
    } catch (error) {
      console.error('[GoogleCalendarService] OAuth callback error:', error);

      const calendarError =
        error instanceof Error && 'type' in error
          ? (error as unknown as CalendarError)
          : calendarErrorHandler.createError(
              CalendarErrorType.AUTH_FAILED,
              error instanceof Error ? error.message : 'Unknown authentication error',
              true,
              'google',
            );

      return {
        success: false,
        error: calendarError,
      };
    }
  }

  /**
   * Exchange authorization code for tokens
   * Private method to handle token exchange with Google
   */
  private async exchangeCodeForTokens(code: string): Promise<{
    success: boolean;
    tokens?: GoogleTokenResponse;
    error?: string;
  }> {
    try {
      const response = await fetch(GoogleCalendarService.GOOGLE_TOKEN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: this.config.clientId,
          client_secret: this.config.clientSecret,
          code,
          grant_type: 'authorization_code',
          redirect_uri: this.config.redirectUri,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[GoogleCalendarService] Token exchange failed:', response.status, errorText);
        return {
          success: false,
          error: `Token exchange failed: ${response.status}`,
        };
      }

      const tokens: GoogleTokenResponse = await response.json();
      return {
        success: true,
        tokens,
      };
    } catch (error) {
      console.error('[GoogleCalendarService] Token exchange error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown token exchange error',
      };
    }
  }

  /**
   * Create calendar connection object from tokens
   * Private method to structure connection data
   */
  private async createConnection(
    userId: string,
    tokens: GoogleTokenResponse,
  ): Promise<CalendarConnection> {
    const provider: CalendarProvider = {
      id: 'google',
      name: 'Google Calendar',
      iconPath: '/static/assets/icons/landing-page/google.svg',
      authUrl: GoogleCalendarService.GOOGLE_AUTH_URL,
      scopes: this.config.scopes,
    };

    const expiresAt = new Date();
    expiresAt.setSeconds(expiresAt.getSeconds() + tokens.expires_in);

    return {
      id: this.generateConnectionId(),
      userId,
      provider,
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      expiresAt,
      createdAt: new Date(),
      lastSyncAt: undefined,
    };
  }

  /**
   * Store calendar connection securely
   * Requirement 5.3: Secure token storage and connection persistence
   */
  private async storeConnection(connection: CalendarConnection): Promise<void> {
    try {
      // Store in localStorage for now - in production this should be encrypted and stored securely
      const storageKey = `calendar_connection_${connection.provider.id}_${connection.userId}`;
      const connectionData = {
        ...connection,
        // Don't store sensitive tokens in localStorage in production
        accessToken: this.encryptToken(connection.accessToken),
        refreshToken: this.encryptToken(connection.refreshToken),
      };

      localStorage.setItem(storageKey, JSON.stringify(connectionData));

      // Also store a reference for quick lookup
      const connectionsKey = `calendar_connections_${connection.userId}`;
      const existingConnections = this.getStoredConnections(connection.userId);
      const updatedConnections = existingConnections.filter(
        (conn) => conn.provider.id !== connection.provider.id,
      );
      updatedConnections.push(connection);

      localStorage.setItem(connectionsKey, JSON.stringify(updatedConnections));

      console.log('[GoogleCalendarService] Connection stored successfully');
    } catch (error) {
      console.error('[GoogleCalendarService] Failed to store connection:', error);
      throw new Error('Failed to store calendar connection');
    }
  }

  /**
   * Retrieve stored calendar connections for a user
   * Requirement 5.4: Connection persistence
   */
  public getStoredConnections(userId: string): CalendarConnection[] {
    try {
      const connectionsKey = `calendar_connections_${userId}`;
      const stored = localStorage.getItem(connectionsKey);

      if (!stored) {
        return [];
      }

      const connections: CalendarConnection[] = JSON.parse(stored);
      return connections.map((conn) => ({
        ...conn,
        // Decrypt tokens when retrieving
        accessToken: this.decryptToken(conn.accessToken),
        refreshToken: this.decryptToken(conn.refreshToken),
        expiresAt: new Date(conn.expiresAt),
        createdAt: new Date(conn.createdAt),
        lastSyncAt: conn.lastSyncAt ? new Date(conn.lastSyncAt) : undefined,
      }));
    } catch (error) {
      console.error('[GoogleCalendarService] Failed to retrieve connections:', error);
      return [];
    }
  }

  /**
   * Get Google Calendar connection for a user
   */
  public getGoogleConnection(userId: string): CalendarConnection | null {
    const connections = this.getStoredConnections(userId);
    return connections.find((conn) => conn.provider.id === 'google') || null;
  }

  /**
   * Refresh expired access token using refresh token
   * Requirement 5.4: Token refresh for connection persistence
   */
  public async refreshAccessToken(
    connection: CalendarConnection,
  ): Promise<CalendarConnection | null> {
    try {
      return await calendarErrorHandler.handleTokenRefresh(connection, connection.userId);
    } catch (error) {
      console.error('[GoogleCalendarService] Token refresh failed:', error);
      return null;
    }
  }

  /**
   * Internal method to refresh token (used by error handler)
   */
  public async _refreshTokenInternal(connection: CalendarConnection): Promise<CalendarConnection> {
    const response = await fetch(GoogleCalendarService.GOOGLE_TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: this.config.clientId,
        client_secret: this.config.clientSecret,
        refresh_token: connection.refreshToken,
        grant_type: 'refresh_token',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[GoogleCalendarService] Token refresh failed:', response.status, errorText);
      throw calendarErrorHandler.createError(
        CalendarErrorType.TOKEN_EXPIRED,
        `Token refresh failed: ${response.status}`,
        response.status >= 500, // Retry server errors
        'google',
      );
    }

    const tokens: Partial<GoogleTokenResponse> = await response.json();

    if (!tokens.access_token) {
      throw calendarErrorHandler.createError(
        CalendarErrorType.AUTH_FAILED,
        'Token refresh response missing access_token',
        false,
        'google',
      );
    }

    const updatedConnection: CalendarConnection = {
      ...connection,
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token || connection.refreshToken,
      expiresAt: new Date(Date.now() + (tokens.expires_in ?? 3600) * 1000),
    };

    // Store updated connection
    await this.storeConnection(updatedConnection);

    return updatedConnection;
  }

  /**
   * Remove stored calendar connection
   */
  public async disconnectCalendar(userId: string): Promise<void> {
    try {
      const storageKey = `calendar_connection_google_${userId}`;
      localStorage.removeItem(storageKey);

      // Update connections list
      const connectionsKey = `calendar_connections_${userId}`;
      const connections = this.getStoredConnections(userId);
      const filteredConnections = connections.filter((conn) => conn.provider.id !== 'google');

      if (filteredConnections.length > 0) {
        localStorage.setItem(connectionsKey, JSON.stringify(filteredConnections));
      } else {
        localStorage.removeItem(connectionsKey);
      }

      console.log('[GoogleCalendarService] Calendar disconnected successfully');
    } catch (error) {
      console.error('[GoogleCalendarService] Failed to disconnect calendar:', error);
      throw new Error('Failed to disconnect calendar');
    }
  }

  /**
   * Basic token encryption (in production, use proper encryption)
   */
  private encryptToken(token: string): string {
    // Simple base64 encoding for demo - use proper encryption in production
    return btoa(token);
  }

  /**
   * Basic token decryption (in production, use proper decryption)
   */
  private decryptToken(encryptedToken: string): string {
    // Simple base64 decoding for demo - use proper decryption in production
    try {
      return atob(encryptedToken);
    } catch {
      return encryptedToken; // Return as-is if not encrypted
    }
  }

  /**
   * Generate unique connection ID
   */
  private generateConnectionId(): string {
    return `google_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Validate if connection is still valid (not expired)
   * Includes 5-minute buffer for proactive token refresh
   */
  public isConnectionValid(connection: CalendarConnection): boolean {
    const now = new Date();
    // Ensure expiresAt is a Date object
    const expiresAt =
      connection.expiresAt instanceof Date ? connection.expiresAt : new Date(connection.expiresAt);

    // Add 5-minute buffer - refresh tokens 5 minutes before expiration
    const bufferMs = 5 * 60 * 1000;
    return now.getTime() < expiresAt.getTime() - bufferMs;
  }

  /**
   * Get valid connection, refreshing token if necessary
   */
  public async getValidConnection(userId: string): Promise<CalendarConnection | null> {
    const connection = this.getGoogleConnection(userId);

    if (!connection) {
      return null;
    }

    if (this.isConnectionValid(connection)) {
      return connection;
    }

    // Token expired, try to refresh
    return await this.refreshAccessToken(connection);
  }

  /**
   * Sync events from Google Calendar
   * Requirement 8.4, 8.5, 8.6: Event synchronization and caching
   */
  public async syncEvents(userId: string): Promise<CalendarEvent[]> {
    return await calendarErrorHandler.handleSyncError(
      'google',
      async () => {
        const connection = await this.getValidConnection(userId);

        if (!connection) {
          throw calendarErrorHandler.createError(
            CalendarErrorType.AUTH_FAILED,
            'No valid Google Calendar connection found',
            false,
            'google',
          );
        }

        const events = await this.fetchEventsFromGoogle(connection);

        // Cache events locally
        await this.cacheEvents(userId, events);

        // Update last sync time
        const updatedConnection = {
          ...connection,
          lastSyncAt: new Date(),
        };
        await this.storeConnection(updatedConnection);

        console.log(`[GoogleCalendarService] Synced ${events.length} events for user ${userId}`);
        return events;
      },
      async () => {
        // Fallback to cached events
        const cachedEvents = await this.getCachedEvents(userId);
        console.log(
          `[GoogleCalendarService] Returning ${cachedEvents.length} cached events as fallback`,
        );
        return cachedEvents;
      },
      userId,
    );
  }

  /**
   * Fetch events from Google Calendar API
   */
  private async fetchEventsFromGoogle(connection: CalendarConnection): Promise<CalendarEvent[]> {
    try {
      // Get primary calendar events for the next 30 days
      const timeMin = new Date().toISOString();
      const timeMax = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      const params = new URLSearchParams({
        timeMin,
        timeMax,
        showDeleted: 'false',
        singleEvents: 'true',
        orderBy: 'startTime',
        maxResults: '250',
      });

      const url = `${GoogleCalendarService.GOOGLE_CALENDAR_API_URL}/calendars/primary/events?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${connection.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[GoogleCalendarService] Events fetch failed:', response.status, errorText);
        throw new Error(`Failed to fetch events: ${response.status}`);
      }

      const data = await response.json();
      return this.transformGoogleEvents(data.items || []);
    } catch (error) {
      console.error('[GoogleCalendarService] Error fetching events:', error);
      throw error;
    }
  }

  /**
   * Transform Google Calendar events to internal format
   */
  private transformGoogleEvents(googleEvents: GoogleCalendarEvent[]): CalendarEvent[] {
    return googleEvents.map((event) => {
      const startTime = new Date(event.start?.dateTime || event.start?.date || 0);
      const endTime = new Date(event.end?.dateTime || event.end?.date || 0);
      const now = new Date();

      let status: 'upcoming' | 'in-progress' | 'completed' = 'upcoming';
      if (now >= startTime && now <= endTime) {
        status = 'in-progress';
      } else if (now > endTime) {
        status = 'completed';
      }

      const attendees: Attendee[] = (event.attendees || []).map((attendee: GoogleAttendee) => ({
        id: attendee.email || '',
        name: attendee.displayName || attendee.email || '',
        email: attendee.email || '',
        avatar: undefined,
        status: this.mapGoogleAttendeeStatus(attendee.responseStatus),
      }));

      return {
        id: event.id || '',
        title: event.summary || 'Untitled Event',
        startTime,
        endTime,
        attendees,
        meetingUrl: this.extractMeetingUrl(event),
        location: event.location,
        description: event.description,
        recurrence: this.parseGoogleRecurrence(event.recurrence),
        status,
      };
    });
  }

  /**
   * Map Google attendee status to internal format
   */
  private mapGoogleAttendeeStatus(
    googleStatus?: string,
  ): 'accepted' | 'declined' | 'tentative' | 'pending' {
    switch (googleStatus) {
      case 'accepted':
        return 'accepted';
      case 'declined':
        return 'declined';
      case 'tentative':
        return 'tentative';
      default:
        return 'pending';
    }
  }

  /**
   * Extract meeting URL from Google Calendar event
   */
  private extractMeetingUrl(event: GoogleCalendarEvent): string | undefined {
    // Check for Google Meet link
    if (event.hangoutLink) {
      return event.hangoutLink;
    }

    // Check for other meeting URLs in description or location
    const text = `${event.description || ''} ${event.location || ''}`;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = text.match(urlRegex);

    if (urls) {
      // Look for common meeting platforms
      const meetingUrl = urls.find(
        (url) =>
          url.includes('zoom.us') ||
          url.includes('teams.microsoft.com') ||
          url.includes('meet.google.com') ||
          url.includes('webex.com') ||
          url.includes('gotomeeting.com'),
      );
      return meetingUrl;
    }

    return undefined;
  }

  /**
   * Parse Google Calendar recurrence rules (simplified)
   */
  private parseGoogleRecurrence(recurrence?: string[]): RecurrencePattern | undefined {
    if (!recurrence || recurrence.length === 0) {
      return undefined;
    }

    // This is a simplified parser - Google's RRULE format is complex
    const rrule = recurrence[0];
    if (rrule.includes('FREQ=DAILY')) {
      return { frequency: 'daily', interval: 1 };
    }
    if (rrule.includes('FREQ=WEEKLY')) {
      return { frequency: 'weekly', interval: 1 };
    }
    if (rrule.includes('FREQ=MONTHLY')) {
      return { frequency: 'monthly', interval: 1 };
    }
    if (rrule.includes('FREQ=YEARLY')) {
      return { frequency: 'yearly', interval: 1 };
    }

    return undefined;
  }

  /**
   * Cache events in local storage
   */
  private async cacheEvents(userId: string, events: CalendarEvent[]): Promise<void> {
    try {
      const cacheKey = `calendar_events_google_${userId}`;
      const cacheData = {
        events,
        cachedAt: new Date().toISOString(),
        ttl: 15 * 60 * 1000, // 15 minutes TTL
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      console.log(`[GoogleCalendarService] Cached ${events.length} events for user ${userId}`);
    } catch (error) {
      console.error('[GoogleCalendarService] Failed to cache events:', error);
    }
  }

  /**
   * Get cached events from local storage
   */
  public async getCachedEvents(userId: string): Promise<CalendarEvent[]> {
    try {
      const cacheKey = `calendar_events_google_${userId}`;
      const cached = localStorage.getItem(cacheKey);

      if (!cached) {
        return [];
      }

      const cacheData = JSON.parse(cached);
      const cachedAt = new Date(cacheData.cachedAt);
      const now = new Date();

      // Check if cache is still valid (within TTL)
      if (now.getTime() - cachedAt.getTime() > cacheData.ttl) {
        console.log('[GoogleCalendarService] Cache expired, removing stale data');
        localStorage.removeItem(cacheKey);
        return [];
      }

      // Transform cached events back to proper Date objects
      const events = cacheData.events.map((event: Record<string, unknown>) => ({
        ...event,
        startTime: new Date(event.startTime as string),
        endTime: new Date(event.endTime as string),
      }));

      return events;
    } catch (error) {
      console.error('[GoogleCalendarService] Failed to get cached events:', error);
      return [];
    }
  }

  /**
   * Clear cached events for a user
   */
  public async clearCachedEvents(userId: string): Promise<void> {
    try {
      const cacheKey = `calendar_events_google_${userId}`;
      localStorage.removeItem(cacheKey);
      console.log(`[GoogleCalendarService] Cleared cached events for user ${userId}`);
    } catch (error) {
      console.error('[GoogleCalendarService] Failed to clear cached events:', error);
    }
  }
}

// Export singleton instance with default configuration
// In production, these should come from environment variables
const env = (import.meta.env ?? {}) as Record<string, string | undefined>;
export const googleCalendarService = new GoogleCalendarService({
  clientId: env.VITE_GOOGLE_CLIENT_ID || '',
  clientSecret: env.VITE_GOOGLE_CLIENT_SECRET || '',
  redirectUri: env.VITE_GOOGLE_REDIRECT_URI || `${window.location.origin}/auth/google/callback`,
  scopes: [
    'https://www.googleapis.com/auth/calendar.readonly',
    'https://www.googleapis.com/auth/calendar.events',
  ],
});
